import classNames from 'classnames'
import React, { useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
  ChevronDoubleRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from '../../../../components/icons'
import { useUser } from '../../../../contexts/UserContext'
import { Path } from '../../../../utils/constants'
import styles from './Login.module.css'

const Login: React.FC = () => {
  const { login } = useUser()
  const history = useHistory()
  const { search } = useLocation()

  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false)
  const [rememberMe, setRememberMe] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const redirect = useMemo(() => {
    const target = new URLSearchParams(search).get('redirect')
    return target ? decodeURIComponent(target) : null
  }, [search])

  const handleForgotMyPassword = () => history.push(Path.RESET_PASSWORD_REQUEST)

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    login(email.toLocaleLowerCase().replaceAll(' ', ''), password, rememberMe)
      .then(({ success, errorMessage }) => {
        if (success) {
          history.push(redirect ?? Path.HOME)
        } else {
          setError(errorMessage)
        }
      })
      .catch((err) => {
        console.error('Error when attempting to login:', err)
        setError('An unexpected error has occurred. Please try again.')
      })
  }

  const handlePasswordVisible = () => setPasswordVisible((cur) => !cur)

  const inputClasses =
    'h-select w-96 max-w-full px-2 text-white placeholder-white bg-secondary-dark focus:outline-none'

  return (
    <div
      className={classNames(
        'flex h-screen items-center justify-center bg-black font-work-sans',
        styles.background
      )}
    >
      <form className="mx-2 flex flex-col items-center" onSubmit={handleSubmit}>
        <span className="text-4xl font-bold text-secondary-dark">Login</span>
        <input
          className={classNames(
            'mt-2 rounded-t-md rounded-b-none border-b-2 border-white',
            inputClasses
          )}
          value={email}
          placeholder="Email Address"
          autoComplete="email"
          onChange={(event) => setEmail(event.target.value)}
        />
        <span className="relative">
          <input
            className={classNames('rounded-t-none rounded-b-md', inputClasses)}
            type={passwordVisible ? 'text' : 'password'}
            value={password}
            placeholder="Password"
            autoComplete="password"
            onChange={(event) => setPassword(event.target.value)}
          />
          <button
            className="pointer absolute top-1/4 right-2 h-4 w-4 text-white"
            type="button"
            onClick={handlePasswordVisible}
          >
            {passwordVisible ? <EyeIcon /> : <EyeSlashIcon />}
          </button>
        </span>
        <button
          className="mt-4 text-secondary-dark hover:text-secondary-main focus:outline-none focus-visible:text-secondary-main"
          type="button"
          onClick={handleForgotMyPassword}
        >
          Forgot my password
        </button>
        <label className="mt-2 flex cursor-pointer select-none items-center text-secondary-dark focus-within:text-secondary-main hover:text-secondary-main">
          <span>Remember me</span>
          <input
            className="ml-2"
            type="checkbox"
            checked={rememberMe}
            onChange={(event) => setRememberMe(event.target.checked)}
          />
        </label>
        {error && <span className="mt-4 font-bold text-red-600">{error}</span>}
        <button
          className="mt-4 flex h-12 w-12 items-center justify-center rounded-full border-2 border-white bg-transparent ring-success-main transition duration-100 hover:ring-2 focus:outline-none focus:ring-2"
          type="submit"
        >
          <ChevronDoubleRightIcon className="h-6 w-6 text-white" />
        </button>
      </form>
    </div>
  )
}

export default Login
